import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeToPush } from '../lib/push';

interface NotifPrefs {
  push_enabled:            boolean;
  email_enabled:           boolean;
  morning_time:            string;
  evening_time:            string;
  timezone:                string;
  daily_forecast:          boolean;
  evening_reflection:      boolean;
  astrological_events:     boolean;
  solar_return:            boolean;
  feature_discovery:       boolean;
  inactivity_reengagement: boolean;
}

const CATEGORY_LABELS: [keyof NotifPrefs, string][] = [
  ['daily_forecast',          'Previsão diária'],
  ['evening_reflection',      'Reflexão noturna'],
  ['astrological_events',     'Eventos astrológicos (lua cheia, retrógrados)'],
  ['solar_return',            'Retorno solar (seu aniversário)'],
  ['feature_discovery',       'Descobertas de novos sistemas'],
  ['inactivity_reengagement', 'Lembretes quando você some por alguns dias'],
];

export function NotificationSettings() {
  const [prefs, setPrefs]   = useState<NotifPrefs | null>(null);
  const [saving, setSaving] = useState(false);
  const [pushOk, setPushOk] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) setPrefs(data as NotifPrefs);
          else {
            // create defaults
            supabase
              .from('notification_preferences')
              .upsert({ user_id: user.id })
              .select('*')
              .single()
              .then(({ data: created }) => created && setPrefs(created as NotifPrefs));
          }
        });
    });
  }, []);

  async function save() {
    if (!prefs) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('notification_preferences')
        .update(prefs)
        .eq('user_id', user.id);
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  async function enablePush() {
    const ok = await subscribeToPush();
    setPushOk(ok);
  }

  function update<K extends keyof NotifPrefs>(key: K, value: NotifPrefs[K]) {
    setPrefs(p => p ? { ...p, [key]: value } : p);
  }

  if (!prefs) {
    return (
      <div className="min-h-screen bg-[#0D1130] flex items-center justify-center">
        <p className="text-[#F5ECD7]/60">Carregando…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1130] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <div className="text-[#C9A84C] text-xl tracking-[8px] mb-2" aria-hidden="true">✦</div>
          <h1 className="text-[#C9A84C] text-3xl font-light mb-2">Notificações</h1>
          <p className="text-[#F5ECD7]/60 text-sm">Configure quando e como o Professya se conecta com você</p>
        </div>

        {/* Channels */}
        <section className="mb-6 p-6 border border-[#C9A84C]/30 rounded-sm">
          <h2 className="text-[#C9A84C] text-lg font-light mb-4">Canais</h2>
          {([
            ['push_enabled', 'Notificações no celular/navegador'],
            ['email_enabled', 'Notificações por email'],
          ] as [keyof NotifPrefs, string][]).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between py-3 border-b border-[#C9A84C]/10 last:border-0">
              <span className="text-[#F5ECD7] text-sm">{label}</span>
              <input
                type="checkbox"
                checked={prefs[key] as boolean}
                onChange={e => update(key, e.target.checked as NotifPrefs[typeof key])}
                className="w-4 h-4 accent-[#C9A84C]"
                aria-label={label}
              />
            </label>
          ))}
          <button
            onClick={enablePush}
            className="mt-4 px-6 py-2 border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-wider hover:bg-[#C9A84C]/10 transition-colors"
          >
            {pushOk ? '✓ Push ativado neste dispositivo' : 'Ativar push neste dispositivo'}
          </button>
        </section>

        {/* Horários */}
        <section className="mb-6 p-6 border border-[#C9A84C]/30 rounded-sm">
          <h2 className="text-[#C9A84C] text-lg font-light mb-4">Horários</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {([
              ['morning_time', 'Mensagem da manhã'],
              ['evening_time', 'Reflexão da noite'],
            ] as [keyof NotifPrefs, string][]).map(([key, label]) => (
              <label key={key} className="block">
                <span className="block text-[#F5ECD7]/70 text-xs uppercase tracking-wider mb-2">{label}</span>
                <input
                  type="time"
                  value={prefs[key] as string}
                  onChange={e => update(key, e.target.value as NotifPrefs[typeof key])}
                  className="bg-transparent border border-[#C9A84C]/30 text-[#F5ECD7] p-2 rounded-sm w-full focus:border-[#C9A84C] focus:outline-none"
                  aria-label={label}
                />
              </label>
            ))}
          </div>
        </section>

        {/* Categories */}
        <section className="mb-8 p-6 border border-[#C9A84C]/30 rounded-sm">
          <h2 className="text-[#C9A84C] text-lg font-light mb-4">O que você quer receber</h2>
          {CATEGORY_LABELS.map(([key, label]) => (
            <label key={key} className="flex items-center justify-between py-3 border-b border-[#C9A84C]/10 last:border-0">
              <span className="text-[#F5ECD7] text-sm">{label}</span>
              <input
                type="checkbox"
                checked={prefs[key] as boolean}
                onChange={e => update(key, e.target.checked as NotifPrefs[typeof key])}
                className="w-4 h-4 accent-[#C9A84C]"
                aria-label={label}
              />
            </label>
          ))}
        </section>

        <button
          onClick={save}
          disabled={saving}
          className="w-full px-8 py-4 bg-[#C9A84C] text-[#0D1130] tracking-[2px] uppercase text-xs font-bold hover:bg-[#C9A84C]/90 disabled:opacity-60 transition-colors"
        >
          {saving ? 'Salvando…' : saved ? '✓ Salvo' : 'Salvar preferências'}
        </button>
      </div>
    </div>
  );
}
